import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/", end: true },
  { label: "电影", to: "/category/movie", end: false },
  { label: "电视剧", to: "/category/tv", end: false },
  { label: "资讯", to: "/news", end: false },
  { label: "专题", to: "/special", end: false },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled && !menuOpen;

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
    setMenuOpen(false);
  };

  const navClass = (isActive: boolean) =>
    [
      "relative flex h-[72px] items-center px-3 text-sm transition-colors duration-300",
      transparent
        ? "text-background-100/85 hover:text-background-50"
        : "text-foreground-700 hover:text-accent-700",
      isActive
        ? transparent
          ? "text-secondary-300 after:absolute after:inset-x-3 after:bottom-4 after:h-[2px] after:rounded-full after:bg-secondary-400"
          : "text-accent-700 after:absolute after:inset-x-3 after:bottom-4 after:h-[2px] after:rounded-full after:bg-accent-500"
        : "",
    ].join(" ");

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        transparent
          ? "bg-gradient-to-b from-primary-950/80 via-primary-950/40 to-transparent"
          : "border-b border-background-200 bg-background-50/95 backdrop-blur-md"
      }`}
    >
      <div className="flex h-[72px] w-full items-center justify-between gap-4 px-4 md:px-6 xl:px-10">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500 text-primary-950">
            <i className="ri-film-line text-lg" />
          </span>
          <span className="leading-tight">
            <span
              className={`block font-heading text-[17px] font-semibold tracking-tight ${
                transparent ? "text-background-50" : "text-foreground-950"
              }`}
            >
              午夜影院
            </span>
            <span
              className={`hidden font-label text-[9px] uppercase tracking-[0.24em] sm:block ${
                transparent ? "text-background-300/80" : "text-foreground-400"
              }`}
            >
              Midnight Cinema
            </span>
          </span>
        </Link>

        <nav className="hidden items-center md:flex" aria-label="主导航">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className={({ isActive }) => navClass(isActive)}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <form
            onSubmit={handleSearch}
            className={`hidden items-center gap-1.5 rounded-md border px-2.5 py-1.5 transition-colors duration-300 md:flex ${
              transparent
                ? "border-background-50/25 bg-primary-950/30 text-background-100"
                : "border-background-300 bg-background-100 text-foreground-700"
            }`}
          >
            <i className="ri-search-line text-sm" />
            <input
              type="search"
              name="q"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影片 / 资讯"
              aria-label="站内搜索"
              className={`w-28 bg-transparent text-xs outline-none lg:w-36 ${
                transparent
                  ? "placeholder:text-background-300/70"
                  : "placeholder:text-foreground-400"
              }`}
            />
          </form>

          <Link
            to="/search"
            aria-label="搜索"
            className={`flex h-9 w-9 items-center justify-center rounded-md border transition-colors duration-300 md:hidden ${
              transparent
                ? "border-background-50/25 text-background-50"
                : "border-background-300 text-foreground-700"
            }`}
          >
            <i className="ri-search-line" />
          </Link>

          <Link
            to="/about"
            className={`hidden whitespace-nowrap rounded-md px-3 py-2 text-xs font-medium transition-colors duration-300 lg:block ${
              transparent
                ? "border border-background-50/25 text-background-100 hover:border-secondary-400 hover:text-secondary-300"
                : "border border-background-300 text-foreground-700 hover:border-accent-400 hover:text-accent-700"
            }`}
          >
            关于我们
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label={menuOpen ? "关闭菜单" : "打开菜单"}
            aria-expanded={menuOpen}
            className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border transition-colors duration-300 md:hidden ${
              transparent
                ? "border-background-50/25 text-background-50"
                : "border-background-300 text-foreground-800"
            }`}
          >
            <i className={menuOpen ? "ri-close-line text-lg" : "ri-menu-3-line text-lg"} />
          </button>
        </div>
      </div>

      {menuOpen ? (
        <div className="border-t border-background-200 bg-background-50 md:hidden">
          <nav className="flex flex-col px-4 py-2" aria-label="移动端导航">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="flex items-center justify-between border-b border-background-200/70 py-3 text-sm text-foreground-800"
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-400" />
              </Link>
            ))}
            <Link
              to="/about"
              className="flex items-center justify-between py-3 text-sm text-foreground-800"
            >
              关于我们
              <i className="ri-arrow-right-s-line text-foreground-400" />
            </Link>
          </nav>
          <form onSubmit={handleSearch} className="flex items-center gap-2 px-4 pb-4">
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2">
              <i className="ri-search-line text-sm text-foreground-500" />
              <input
                type="search"
                name="q"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影片 / 资讯"
                aria-label="站内搜索"
                className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-accent-500 px-3 py-2 text-xs font-medium text-primary-950"
            >
              搜索
            </button>
          </form>
        </div>
      ) : null}
    </header>
  );
}